import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import type { Database, PurchaseLineInput, PurchaseRecordMetadata } from '@klothiq/domain';
import { useOperations } from './useOperations';
import { operationsRpc } from '@/lib/operations';
export type Purchase = Database['public']['Tables']['purchases']['Row'] &
  Partial<PurchaseRecordMetadata>;
export type PurchaseItem = Database['public']['Tables']['purchase_items']['Row'];
export interface PurchaseWithSupplier extends Purchase {
  suppliers: { name: string; phone: string | null } | null;
}
export const PURCHASES_KEY = 'purchases';
async function purchasePages<T>(
  page: (start: number, end: number) => PromiseLike<{ data: unknown[] | null; error: unknown }>,
) {
  const rows: T[] = [];
  for (let start = 0; ; start += 500) {
    const { data, error } = await page(start, start + 499);
    if (error) throw purchaseError(error);
    rows.push(...((data || []) as T[]));
    if (!data || data.length < 500) return rows;
  }
}
export function purchaseError(error: unknown): Error {
  const e = error as { message?: string; code?: string };
  if (e?.code === 'PGRST116') return new Error('Purchase not found or unavailable to this login.');
  return new Error(
    e?.code === 'PGRST202' || e?.code === '42703'
      ? 'The purchase database update is required. Ask your administrator to apply the purchase migration.'
      : e?.message || 'The purchase could not be updated. Your entries have been kept.',
  );
}
export function usePurchaseWorkspace() {
  const { organization, can, profile, access } = useAuth();
  return useQuery({
    queryKey: ['purchase_workspace', organization?.id, profile?.id, access?.access_version],
    enabled: !!organization && can('purchases.view'),
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('purchase_workspace_contract');
      if (error && error.code !== 'PGRST202') throw purchaseError(error);
      return !error && data?.version === 2;
    },
  });
}
export function usePurchases(filters?: {
  status?: string;
  search?: string;
  supplier?: string;
  payment?: string;
  mode?: string;
  from?: string;
  to?: string;
  lifecycle?: 'active' | 'archived' | 'deleted';
}) {
  const operations = useOperations();
  const { organization, can, profile, access } = useAuth();
  const orgId = organization?.id;
  return useQuery({
    queryKey: [
      PURCHASES_KEY,
      orgId,
      profile?.id,
      access?.access_version,
      {
        status: filters?.status,
        supplier: filters?.supplier,
        payment: filters?.payment,
        from: filters?.from,
        to: filters?.to,
        lifecycle: filters?.lifecycle,
        operations: operations.data?.modules.purchases,
      },
    ],
    enabled: !!orgId && can('purchases.view'),
    staleTime: 15000,
    queryFn: async (): Promise<PurchaseWithSupplier[]> => {
      if (operations.data?.modules.purchases === 1) {
        const rows: PurchaseWithSupplier[] = [];
        for (let offset = 0; ; offset += 500) {
          const result = await operationsRpc<{ rows: PurchaseWithSupplier[]; total: number }>(
            'list_purchase_records_v1',
            { p_filters: { ...filters, search: undefined, mode: undefined, offset, limit: 500 } },
          );
          rows.push(...result.rows);
          if (offset + result.rows.length >= result.total) return rows;
          if (!result.rows.length) throw new Error('Purchases changed while loading. Reload.');
        }
      }
      if (filters?.lifecycle && filters.lifecycle !== 'active') return [];
      const rows: PurchaseWithSupplier[] = [];
      for (let start = 0; ; start += 500) {
        let q = supabase
          .from('purchases')
          .select('*, suppliers(name, phone)')
          .eq('organization_id', orgId!)
          .eq('is_voided', false)
          .order('purchase_date', { ascending: false })
          .order('id')
          .range(start, start + 499);
        if (filters?.status && filters.status !== 'all') q = q.eq('status', filters.status);
        if (filters?.supplier) q = q.eq('supplier_id', filters.supplier);
        if (filters?.payment && filters.payment !== 'all')
          q = q.eq('payment_status', filters.payment);
        if (filters?.from) q = q.gte('purchase_date', filters.from);
        if (filters?.to) q = q.lte('purchase_date', filters.to);
        const { data, error } = await q;
        if (error) throw purchaseError(error);
        rows.push(...((data ?? []) as PurchaseWithSupplier[]));
        if (!data || data.length < 500) break;
      }
      return rows;
    },
    select: (rows) => {
      const search = filters?.search?.trim().toLowerCase();
      return rows.filter(
        (p) =>
          (!filters?.mode ||
            filters.mode === 'all' ||
            (p.details_mode || p.entry_mode || 'catalog') === filters.mode) &&
          (!search ||
            [p.purchase_number, p.supplier_invoice_no, p.suppliers?.name].some((v) =>
              v?.toLowerCase().includes(search),
            )),
      );
    },
  });
}
export function usePurchasePeople(enabled: boolean) {
  const { organization, profile, access, can } = useAuth();
  return useQuery({
    queryKey: ['purchases', 'people', organization?.id, profile?.id, access?.access_version],
    enabled: enabled && !!organization && can('purchases.view'),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('purchase_people');
      if (error) throw purchaseError(error);
      return (data || []) as Array<{ id: string; full_name: string }>;
    },
  });
}
export interface PurchaseDetail extends Purchase {
  suppliers: { id: string; name: string; phone: string | null; city: string | null } | null;
  purchase_items: Array<
    PurchaseItem & {
      product_variants: {
        sku: string;
        size: string;
        color: string | null;
        products: { name: string; product_code: string } | null;
      } | null;
    }
  >;
}
export function usePurchase(purchaseId?: string) {
  const { organization, profile, access, can } = useAuth();
  return useQuery({
    queryKey: [PURCHASES_KEY, organization?.id, profile?.id, access?.access_version, purchaseId],
    enabled: !!organization && !!purchaseId && can('purchases.view'),
    queryFn: async (): Promise<PurchaseDetail> => {
      const { data, error } = await supabase
        .from('purchases')
        .select('*, suppliers(id,name,phone,city)')
        .eq('id', purchaseId!)
        .eq('organization_id', organization!.id)
        .single();
      if (error) throw purchaseError(error);
      const items = await purchasePages<PurchaseDetail['purchase_items'][number]>((start, end) => {
        let itemsQuery = supabase
          .from('purchase_items')
          .select('*, product_variants(sku,size,color,products(name,product_code))')
          .eq('organization_id', organization!.id)
          .eq('purchase_id', purchaseId!);
        if ('entry_mode' in data) itemsQuery = itemsQuery.order('position');
        return itemsQuery.order('created_at').order('id').range(start, end);
      });
      const p = { ...data, purchase_items: items } as PurchaseDetail;
      return {
        ...p,
        entry_mode: p.entry_mode || 'catalog',
        purchase_items: p.purchase_items
          .filter((i) => !i.is_removed)
          .map((i) => ({
            ...i,
            item_kind: i.item_kind || 'catalog',
            inventory_treatment: i.inventory_treatment || 'tracked',
          })),
      };
    },
  });
}
export interface PurchasePayment {
  created_by: string | null;
  updated_by: string | null;
  id: string;
  purchase_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  account_id: string;
  reference_number: string | null;
  notes: string | null;
  is_void: boolean;
  updated_at: string;
  cash_txn_id: string;
  cash_accounts: { name: string } | null;
}
export function usePurchasePayments(id?: string) {
  const { organization, can, profile, access } = useAuth();
  return useQuery({
    queryKey: ['purchases', 'payments', id, organization?.id, profile?.id, access?.access_version],
    enabled: !!id && !!organization && can('purchases.view'),
    queryFn: async () => {
      return purchasePages<PurchasePayment>((start, end) =>
        supabase
          .from('payments')
          .select('*, cash_accounts(name)')
          .eq('organization_id', organization!.id)
          .eq('purchase_id', id!)
          .eq('payment_type', 'purchase_payment')
          .order('created_at')
          .order('id')
          .range(start, end),
      );
    },
  });
}
export function usePurchaseReceipts(id?: string, enabled = false) {
  const { organization, can, profile, access } = useAuth();
  return useQuery({
    queryKey: ['purchases', 'receipts', id, organization?.id, profile?.id, access?.access_version],
    enabled: !!id && !!organization && enabled && can('purchases.view'),
    queryFn: async () => {
      return purchasePages<{
        id: string;
        created_at: string;
        created_by: string;
        notes: string | null;
        purchase_receipt_items: Array<{
          purchase_item_id: string;
          quantity: number;
          inventory_treatment: string;
          unit_cost: number;
        }>;
      }>((start, end) =>
        supabase
          .from('purchase_receipts')
          .select(
            'id,created_at,created_by,notes,purchase_receipt_items(purchase_item_id,quantity,inventory_treatment,unit_cost)',
          )
          .eq('organization_id', organization!.id)
          .eq('purchase_id', id!)
          .order('created_at', { ascending: false })
          .order('id')
          .range(start, end),
      );
    },
  });
}
export interface PurchaseInput {
  request_key: string;
  expected_updated_at?: string;
  purchase_id?: string;
  supplier_id: string;
  purchase_date: string;
  expected_date?: string | null;
  supplier_invoice_no?: string;
  entry_mode?: 'catalog' | 'direct';
  status?: string;
  other_charges?: number;
  notes?: string;
  items: PurchaseLineInput[];
  payment?: Record<string, unknown>;
  payment_edits?: Array<Record<string, unknown>>;
  duplicate_invoice_confirmed?: boolean;
}
export interface ReceiveItemInput {
  purchase_item_id: string;
  received_quantity: number;
}
export function usePurchaseOperation(operation: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const { data, error } = await supabase.rpc(operation, { p_data: input });
      if (error) throw purchaseError(error);
      if (!data?.success)
        throw new Error('Purchase update was not confirmed. Reload before retrying.');
      return data as { success: boolean; purchase_id: string };
    },
    onSuccess: () => refreshSaleConsumers(client),
  });
}
export function useCreateOrUpdatePurchase() {
  const op = usePurchaseOperation('create_or_update_purchase');
  return {
    ...op,
    mutateAsync: (input: PurchaseInput) =>
      op.mutateAsync(input as unknown as Record<string, unknown>),
  };
}
export function useReceivePurchase() {
  return usePurchaseOperation('receive_purchase');
}
